import { FACTIII_SPACE_SLUG } from './factiiis';

// Auto-tagging of posts (.specs/auto-tag.md): every public post gets one mood
// and at most two topics, chosen by a small model and paid for by the platform.
//
// The lists live here, shared, because three sides read them: the server
// creates one factiii per entry and offers the slugs to the model, the parser
// below keeps only slugs that are on the lists, and the clients use the slugs
// to tell an automatic tag from one a person placed.

/** The factiii people see as the tool that does the tagging. */
export const AUTO_TAG_TOOL_SLUG = 'auto-tag';

/** A post never gets more topics than this, whatever the model answers. */
export const AUTO_TAG_MAX_TOPICS = 2;

// The account every automatic tag is written as. The server's own constant is
// FACTIII_AI_USERNAME (apps/server/src/constants/bot.ts); the clients cannot
// import that, and tests/autoTag/autoTag.test.ts holds the two equal.
export const AUTO_TAG_AUTHOR_USERNAME = 'factiii-bot';

export type AutoTagEntry = {
  name: string;
  slug: string;
  // One line. The model reads it as the rule for picking the entry, and it is
  // the description of the factiii a person opens.
  description: string;
};

// The first four are the moods the Sentiment Analysis bot already tags, so a
// database that has them keeps one Happy, not two.
export const AUTO_TAG_MOODS = [
  {
    name: 'Happy',
    slug: 'happy',
    description: 'The post is glad, grateful, proud, excited or hopeful.'
  },
  {
    name: 'Sad',
    slug: 'sad',
    description: 'The post is sorrowful, disappointed, lonely or grieving.'
  },
  {
    name: 'Angry',
    slug: 'angry',
    description: 'The post is angry, outraged, frustrated or hostile.'
  },
  {
    name: 'Neutral',
    slug: 'neutral',
    description:
      'The post states or asks something without a clear feeling either way.'
  },
  {
    name: 'Laughing',
    slug: 'laughing',
    description: 'The post is joking, playful, absurd or meant to be funny.'
  }
] as const satisfies readonly AutoTagEntry[];

export const AUTO_TAG_TOPICS = [
  {
    name: 'Politics',
    slug: 'politics',
    description: 'Government, elections, parties, policy and politicians.'
  },
  {
    name: 'World News',
    slug: 'world-news',
    description: 'Current events, international affairs, conflicts, disasters.'
  },
  {
    name: 'Business',
    slug: 'business',
    description: 'Companies, startups, management, deals and industry news.'
  },
  {
    name: 'Economy',
    slug: 'economy',
    description: 'Inflation, jobs, markets, trade and economic policy.'
  },
  {
    name: 'Technology',
    slug: 'technology',
    description: 'Software, hardware, the internet, gadgets and programming.'
  },
  {
    name: 'AI',
    slug: 'ai',
    description: 'Artificial intelligence, machine learning, models and bots.'
  },
  {
    name: 'Science',
    slug: 'science',
    description: 'Research and discoveries in physics, biology, space and more.'
  },
  {
    name: 'Health',
    slug: 'health',
    description: 'Medicine, illness, mental health, nutrition and wellbeing.'
  },
  {
    name: 'Fitness',
    slug: 'fitness',
    description: 'Exercise, training, running, lifting and getting in shape.'
  },
  {
    name: 'Food',
    slug: 'food',
    description: 'Cooking, recipes, restaurants, drinks and eating.'
  },
  {
    name: 'Travel',
    slug: 'travel',
    description: 'Trips, places, flights, hotels and living abroad.'
  },
  {
    name: 'Sports',
    slug: 'sports',
    description: 'Teams, athletes, games, leagues and competitions.'
  },
  {
    name: 'Gaming',
    slug: 'gaming',
    description: 'Video games, consoles, esports and tabletop games.'
  },
  {
    name: 'Movies & TV',
    slug: 'movies-tv',
    description: 'Films, series, streaming, actors and reviews.'
  },
  {
    name: 'Music',
    slug: 'music',
    description: 'Songs, albums, artists, concerts and instruments.'
  },
  {
    name: 'Books',
    slug: 'books',
    description: 'Reading, novels, authors, writing and literature.'
  },
  {
    name: 'Art & Design',
    slug: 'art-design',
    description: 'Drawing, painting, illustration, graphic and product design.'
  },
  {
    name: 'Photography',
    slug: 'photography',
    description: 'Taking photos, cameras, editing and photo sharing.'
  },
  {
    name: 'Fashion',
    slug: 'fashion',
    description: 'Clothing, style, beauty and accessories.'
  },
  {
    name: 'Humor',
    slug: 'humor',
    description: 'Jokes, memes, satire and funny stories.'
  },
  {
    name: 'Relationships',
    slug: 'relationships',
    description: 'Dating, friendship, marriage and family ties.'
  },
  {
    name: 'Parenting',
    slug: 'parenting',
    description: 'Raising children, pregnancy, schooling and family life.'
  },
  {
    name: 'Education',
    slug: 'education',
    description: 'School, university, studying, teaching and learning.'
  },
  {
    name: 'Careers',
    slug: 'careers',
    description: 'Jobs, hiring, the workplace and professional growth.'
  },
  {
    name: 'Personal Finance',
    slug: 'personal-finance',
    description: 'Saving, budgeting, investing, debt and taxes for a person.'
  },
  {
    name: 'Crypto',
    slug: 'crypto',
    description: 'Cryptocurrency, blockchains, tokens and Web3.'
  },
  {
    name: 'Environment',
    slug: 'environment',
    description: 'Climate, energy, nature, pollution and conservation.'
  },
  {
    name: 'Religion & Philosophy',
    slug: 'religion-philosophy',
    description: 'Faith, spirituality, ethics and the big questions.'
  },
  {
    name: 'History',
    slug: 'history',
    description: 'Past events, historical figures and how things came to be.'
  },
  {
    name: 'Pets & Animals',
    slug: 'pets-animals',
    description: 'Pets, wildlife and animal care.'
  },
  {
    name: 'Home & DIY',
    slug: 'home-diy',
    description: 'Housing, repairs, gardening, decorating and making things.'
  },
  {
    name: 'Cars',
    slug: 'cars',
    description: 'Cars, motorcycles, driving and the auto industry.'
  }
] as const satisfies readonly AutoTagEntry[];

export type AutoTagMoodSlug = (typeof AUTO_TAG_MOODS)[number]['slug'];
export type AutoTagTopicSlug = (typeof AUTO_TAG_TOPICS)[number]['slug'];

export type AutoTagAnswer = {
  mood: AutoTagMoodSlug | null;
  topics: AutoTagTopicSlug[];
};

const moodSlugs = new Set<string>(AUTO_TAG_MOODS.map((mood) => mood.slug));
const topicSlugs = new Set<string>(AUTO_TAG_TOPICS.map((topic) => topic.slug));

const isMoodSlug = (value: unknown): value is AutoTagMoodSlug =>
  typeof value === 'string' && moodSlugs.has(value);

const isTopicSlug = (value: unknown): value is AutoTagTopicSlug =>
  typeof value === 'string' && topicSlugs.has(value);

/** True for a mood or topic slug, the only tags the tool writes. */
export const isAutoTagSlug = (slug: string): boolean =>
  moodSlugs.has(slug) || topicSlugs.has(slug);

/**
 * True when a post carries this factiii as an automatic tag: it is a mood or
 * topic of the `factiii` space and factiii-bot placed one of its tags. The
 * clients use it to label the tag and to offer the author its removal; the
 * server decides again in `factiiis.removeAutoTag`.
 */
export const isAutoTagged = (
  factiii: { slug: string; spaceSlug?: string | null },
  tags: readonly { user?: { username: string } | null }[]
): boolean =>
  factiii.spaceSlug === FACTIII_SPACE_SLUG &&
  isAutoTagSlug(factiii.slug) &&
  tags.some((tag) => tag.user?.username === AUTO_TAG_AUTHOR_USERNAME);

// A model asked for bare JSON still wraps it in a fence now and then.
const CODE_FENCE = /^```(?:json)?\s*([\s\S]*?)\s*```$/i;

/**
 * Reads the model's answer, which must be
 * `{"mood":"<slug>","topics":["<slug>",...]}`. Only slugs on the lists survive:
 * the model cannot invent a tag. Anything that is not that object is "no tags",
 * never an error, so a bad answer costs the post nothing.
 */
export const parseAutoTagAnswer = (raw: string): AutoTagAnswer => {
  const nothing: AutoTagAnswer = { mood: null, topics: [] };
  if (typeof raw !== 'string') return nothing;

  const trimmed = raw.trim();
  const json = CODE_FENCE.exec(trimmed)?.[1] ?? trimmed;

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return nothing;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
    return nothing;

  const { mood, topics } = parsed as { mood?: unknown; topics?: unknown };
  const listed = Array.isArray(topics) ? topics.filter(isTopicSlug) : [];

  return {
    mood: isMoodSlug(mood) ? mood : null,
    topics: [...new Set(listed)].slice(0, AUTO_TAG_MAX_TOPICS)
  };
};
